import React, { useMemo, useState } from 'react';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import { ParsedRoomRow, matchRoomAsset, parsePastedOffer } from './parsePastedOffer';
import type { RoomAssetLink } from './types';

type PastePreviousOfferDialogProps = {
  assets: Array<RoomAssetLink>;
  linkedAssetIds: Array<string>;
  onApply: (roomRows: Array<ParsedRoomRow>) => void;
  onClose: () => void;
  open: boolean;
};

export default function PastePreviousOfferDialog({
  assets,
  linkedAssetIds,
  onApply,
  onClose,
  open,
}: PastePreviousOfferDialogProps) {
  const [pastedText, setPastedText] = useState('');

  const parsed = useMemo(() => (pastedText.trim() ? parsePastedOffer(pastedText) : undefined), [pastedText]);

  const preview = useMemo(() => {
    if (!parsed) {
      return undefined;
    }
    const unmatched: Array<string> = [];
    const ambiguous: Array<string> = [];
    for (const row of parsed.roomRows) {
      const match = matchRoomAsset(row.name, assets, linkedAssetIds);
      if (!match) {
        unmatched.push(row.name);
      } else if (!('asset' in match)) {
        ambiguous.push(row.name);
      }
    }
    return { matched: parsed.roomRows.length - unmatched.length - ambiguous.length, unmatched, ambiguous };
  }, [assets, linkedAssetIds, parsed]);

  const alert = useMemo((): { severity: 'error' | 'info'; message: string } | undefined => {
    if (!parsed || !preview) {
      return undefined;
    }
    if (parsed.roomRows.length === 0) {
      return { severity: 'error', message: 'No room data found — check the format.' };
    }
    const skipped = [
      preview.unmatched.length
        ? `${preview.unmatched.length} skipped (no matching asset): ${preview.unmatched.join(', ')}`
        : '',
      preview.ambiguous.length
        ? `${preview.ambiguous.length} skipped (several assets share the name — add the room first): ${preview.ambiguous.join(', ')}`
        : '',
    ]
      .filter(Boolean)
      .join(', ');
    if (preview.matched === 0) {
      return { severity: 'error', message: `No rows match a room asset — nothing to apply: ${skipped}` };
    }
    const losSummary = parsed.losColumns.length
      ? ` · LOS: ${parsed.losColumns.map((column) => `${column.los}N`).join(', ')}`
      : '';
    return {
      severity: 'info',
      message: `${preview.matched} room${preview.matched === 1 ? '' : 's'} matched${skipped ? `, ${skipped}` : ''}${losSummary}`,
    };
  }, [parsed, preview]);

  function handleApply() {
    if (parsed) {
      onApply(parsed.roomRows);
    }
    onClose();
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: () => setPastedText('') } }}
    >
      <DialogTitle>Paste previous offer</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          Paste a table copied from Excel or a previous offer. The tool will match room names against the account&apos;s
          room assets, adding any room types that aren&apos;t in the table yet and filling in sell prices for each LOS.
          Rows that don&apos;t match a room asset, or match several assets with the same name, are skipped.
        </Typography>

        <TextField
          fullWidth
          minRows={8}
          multiline
          onChange={(e) => setPastedText(e.target.value)}
          placeholder={'Room Type\t3\t5\t7\nDeluxe King\t1299\t1899\t2499'}
          sx={{ mt: 2, '& textarea': { fontFamily: 'monospace' } }}
          value={pastedText}
        />

        {alert && (
          <Alert severity={alert.severity} sx={{ mt: 2 }}>
            {alert.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={!preview?.matched} onClick={handleApply} variant="contained">
          Apply prices
        </Button>
      </DialogActions>
    </Dialog>
  );
}
